import { filterFieldsEnum } from "./filterFieldsEnum";

export const filterKeyEntityEnum = {
  FINDING: "finding",
  REMEDIATION_ITEM: "remediation_item",
} as const;

export type FilterKeyEntity =
  (typeof filterKeyEntityEnum)[keyof typeof filterKeyEntityEnum];

export interface FilterKeyParts {
  entity?: FilterKeyEntity;
  property: string;
}

const filterKeyEntities: readonly string[] = Object.values(filterKeyEntityEnum);

export const filterKeyEntityLabels: Record<FilterKeyEntity, string> = {
  [filterKeyEntityEnum.FINDING]: "Finding",
  [filterKeyEntityEnum.REMEDIATION_ITEM]: "Remediation Item",
};

export const filterKeyEntityClassNames: Record<FilterKeyEntity, string> = {
  [filterKeyEntityEnum.FINDING]: "is-entity-finding",
  [filterKeyEntityEnum.REMEDIATION_ITEM]: "is-entity-remediation-item",
};

const isFilterKeyEntity = (value: string): value is FilterKeyEntity =>
  filterKeyEntities.includes(value);

export const splitFilterKey = (
  key: string | null | undefined
): FilterKeyParts => {
  if (!key) {
    return { property: "" };
  }

  const [prefix, ...propertyParts] = key.split(".");

  if (!propertyParts.length || !isFilterKeyEntity(prefix)) {
    return { property: key };
  }

  return { entity: prefix, property: propertyParts.join(".") };
};

export const isBareFilterKey = (key: string | null | undefined): boolean =>
  !!key && !splitFilterKey(key).entity;

export const joinFilterKey = ({ entity, property }: FilterKeyParts): string =>
  entity ? `${entity}.${property}` : property;

export const FINDING_ACTUAL_STATUS_FILTER_KEY = joinFilterKey({
  entity: filterKeyEntityEnum.FINDING,
  property: filterFieldsEnum.ACTUAL_STATUS,
});

export const filterByProperty = <T extends { property: unknown }>(
  items: readonly T[] | null | undefined,
  property: T["property"]
): T[] => items?.filter((item) => item.property === property) ?? [];

export const pickPreferredEntity = <T extends Pick<FilterKeyParts, "entity">>(
  candidates: readonly T[]
): T | undefined =>
  candidates.find(
    ({ entity }) => entity === filterKeyEntityEnum.REMEDIATION_ITEM
  ) ??
  candidates.find(({ entity }) => entity === filterKeyEntityEnum.FINDING) ??
  candidates[0];

export const isActualStatusKey = (key: string | null | undefined): boolean =>
  splitFilterKey(key).property === filterFieldsEnum.ACTUAL_STATUS;

const toRemediationItemFilterKey = (key: string): string => {
  const { entity = filterKeyEntityEnum.REMEDIATION_ITEM, property } =
    splitFilterKey(key);

  return joinFilterKey({ entity, property });
};

export const isSameRemediationItemFilterKey = (
  key: string | null | undefined,
  otherKey: string | null | undefined
): boolean =>
  !!key &&
  !!otherKey &&
  toRemediationItemFilterKey(key) === toRemediationItemFilterKey(otherKey);
